<script setup>
  import {computed, ref} from "vue";

  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientDelete",
  });

  const props = defineProps({
    patient: {
      type: Object,
      default: () => ({}),
    },
  });

  const page = usePage();

  const refConfirmationDialog = ref(null);

  const form = useForm({
    ids: [props.patient.id],
  });

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    const params = deletePermanently.value ? {action: "forceDelete"} : {};

    form.delete(route("patients.destroy", params));
  }

  const deletePermanently = computed(
    () => props.is_fake || page.props?.client?.license?.license_name === "connect-core",
  );

  const isDeletable = computed(() => {
    if (props.patient.from_wawi) {
      return page.props.can.delete_wawi_patients && props.patient.wawi_records_count === 0;
    }

    return page.props.can.delete_patients;
  });

  const deleteText = computed(() => {
    if (isDeletable.value) {
      return "Sie können die Patientenakte inkl. aller enthaltenen Daten löschen.";
    }

    if (props.patient.from_wawi) {
      if (page.props.can.delete_wawi_patients) {
        return "Sie können den Patienten nicht löschen, da dieser noch Medikationsanalysen aus der Warenwirtschaft hat.";
      }

      return "Sie können den Patienten nicht löschen, da dieser aus der Warenwirtschaft übertragen wurde.";
    }

    return "Sie können den Patienten nicht löschen, da Berechtigungen fehlen.";
  });
</script>

<template>
  <ComponentCard
    title="Patientenakte löschen"
    :subtitle="deleteText"
    class="rounded-lg border-2 border-gray-200"
    :test-id="getTestId('patient-delete')"
  >
    <template v-if="isDeletable" #actions>
      <MedButton
        :disabled="form.processing"
        :data-test="getTestId('patient-delete-card-delete-button')"
        @click="confirmRemove()"
      >
        Löschen
      </MedButton>
    </template>

    <ComponentConfirmationDialog
      ref="refConfirmationDialog"
      :title="deletePermanently ? 'Patienten endgültig löschen?' : 'Patienten löschen?'"
      :content="
        deletePermanently
          ? 'Die Patientenakte wird mit allen enthaltenen Daten endgültig und unwiederbringlich gelöscht.'
          : 'Die Patientenakte wird mit allen zugehörigen Medikationsanalysen in „Gelöschte Patienten“ verschoben.'
      "
      @confirmed="remove()"
    />
  </ComponentCard>
</template>
